import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const sections = [
  {
    id: "rules",
    title: "1. AURAYS RULES",
    icon: "ri-user-settings-line",
    custom1: true,
  },
  {
    id: "content",
    title: "2. TYPES OF CONTENT",
    icon: "ri-layout-grid-line",
    custom2: true,
  },
  {
    id: "restrictions",
    title: "3. RESTRICTIONS ON THE APP",
    icon: "ri-error-warning-line",
    custom3: true,
  },
  {
    id: "privacy",
    title: "4. PRIVACY",
    icon: "ri-lock-line",
    paragraphs: [
      "For information about how Soul Media Limited collects, uses, and shares your personal data, please check out our Privacy Policy. By using Aurays, you acknowledge that we may use such data in accordance with our Privacy Policy.",
    ],
  },
  {
    id: "payment",
    title: "5. PAYMENT TERMS",
    icon: "ri-bank-card-line",
    custom5: true,
  },
  {
    id: "virtual",
    title: "6. VIRTUAL ITEMS",
    icon: "ri-copper-coin-line",
    custom6: true,
  },
  {
    id: "notifications",
    title: "7. PUSH NOTIFICATIONS; LOCATION-BASED FEATURES",
    icon: "ri-notification-3-line",
    custom7: true,
  },
  {
    id: "disclaimer",
    title: "8. DISCLAIMER",
    icon: "ri-scales-3-line",
    custom8: true,
  },
  {
    id: "liability",
    title: "9. LIMITATION OF LIABILITY",
    icon: "ri-prohibited-line",
    custom9: true,
  },
  {
    id: "indemnity",
    title: "10. INDEMNITY",
    icon: "ri-shield-user-line",
    custom10: true,
  },
  {
    id: "copyright",
    title: "11. COPYRIGHT INFRINGEMENT CLAIMS AND TAKEDOWN NOTICES",
    icon: "ri-copyright-line",
    custom11: true,
  },
  {
    id: "appstore",
    title: "12. THIRD PARTY APP STORE",
    icon: "ri-app-store-line",
    custom12: true,
  },
  {
    id: "dispute",
    title: "13. DISPUTE RESOLUTION",
    icon: "ri-hammer-line",
    custom13: true,
  },
  {
    id: "termination",
    title: "14. TERMINATION AND REMEDIES",
    icon: "ri-logout-box-line",
    custom14: true,
  },
  {
    id: "miscellaneous",
    title: "15. MISCELLANEOUS",
    icon: "ri-more-line",
    custom15: true,
  },
  {
    id: "governing",
    title: "16. GOVERNING LAW AND FORUM",
    icon: "ri-government-line",
    paragraphs: [
      'Subject to any mandatory consumer protection laws that may apply in your country or territory of residence which cannot be contracted out of by law, these Terms, your access to and use of the App, all App Content, Member Content, and any claims or disputes arising from or related to your relationship with Soul Media Limited shall be governed by, and construed in accordance with, the laws of the Hong Kong Special Administrative Region ("Hong Kong"), without regard to its conflict of law principles.',
      "Any dispute, claim, or controversy arising out of or relating to these Terms, or the breach, termination, enforcement, interpretation, or validity thereof—including any claims that are for whatever reason not submitted to arbitration or cases challenging the enforceability or applicability of the arbitration provisions herein—shall be subject to the exclusive jurisdiction of the courts of the Hong Kong Special Administrative Region. You and Soul Media Limited expressly consent to the personal jurisdiction and venue of the Hong Kong courts and waive any objection based upon an inconvenient forum (forum non conveniens).",
    ],
  },
  {
    id: "company",
    title: "17. SOUL MEDIA LIMITED",
    icon: "ri-building-line",
    paragraphs: [
      'The Terms constitute a binding legal agreement between you as user ("you") and Soul Media Limited ("we" or "us").',
      "Effective date: These Terms were last updated on: 31st May, 2026.",
    ],
  },
];

export default function TermsAndConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for sticky nav
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-[radial-gradient(ellipse_at_top,_#E6D9FF_0%,_transparent_65%)] opacity-40" />
        </div>
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center relative">
          <div className="inline-flex items-center gap-2 bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-scales-3-line"></i>
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-5">Terms & Conditions</h1>
          <p className="text-gray-500 text-base leading-relaxed max-w-xl mx-auto mb-4">
            Welcome to Aurays. These terms govern your use of our platform and services. Please read them carefully.
          </p>
          <p className="text-xs text-gray-400">
            Last updated: <strong className="text-gray-500">May 2026</strong> &nbsp;·&nbsp; Effective:{" "}
            <strong className="text-gray-500">May 2026</strong>
          </p>
        </div>
      </section>

      {/* Quick nav */}
      <section className="bg-white border-y border-gray-100 py-5 sticky top-[64px] z-30">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400 font-medium mr-1 whitespace-nowrap">Jump to:</span>
            {sections.slice(0, 6).map((section) => (
              <span
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="text-xs bg-[#F5F0FF] text-[#7C55CC] px-3 py-1 rounded-full cursor-pointer hover:bg-[#E6D9FF] transition-colors whitespace-nowrap"
              >
                {section.title.split(". ")[1]}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {/* Intro */}
          <div className="bg-[#F5F0FF] rounded-3xl p-7 mb-10 border border-[#E6D9FF]">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E6D9FF] flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-[#7C55CC] text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#4A2D8A] mb-1">Legal Agreement</p>
                <div className="text-sm text-[#5B3FA0] leading-relaxed flex flex-col gap-3">
                  <p>
                    Welcome to Aurays&apos;s Terms and Conditions of Use (<strong>&ldquo;these Terms&rdquo;</strong>).
                    This is a legal contract between you and Soul Media Limited (
                    <strong>
                      &ldquo;the Company,&rdquo; &ldquo;Aurays,&rdquo; &ldquo;we,&rdquo; or &ldquo;us&rdquo;
                    </strong>
                    ), a company incorporated under the laws of the Hong Kong Special Administrative Region (
                    <strong>&ldquo;Hong Kong&rdquo;</strong>).
                  </p>
                  <p>
                    Please take a few moments to read these Terms before enjoying the Aurays application or website (
                    <strong>the &ldquo;App&rdquo;</strong>). By accessing, viewing, or using the App, you agree to be
                    legally bound by these Terms. If you do not agree, you must not use the App. Please also read our{" "}
                    <Link to="/privacy-policy" className="underline font-semibold">
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link to="/community-guidelines" className="underline font-semibold">
                      Community Guidelines
                    </Link>{" "}
                    (which form part of these Terms).
                  </p>
                  <p>
                    Please be aware that if you subscribe to services for a term (
                    <strong>the &ldquo;Initial Term&rdquo;</strong>), then the terms of your subscription will be
                    automatically renewed for additional periods of the same duration as the Initial Term at
                    Aurays&apos;s then-current fee for such services, unless you cancel your subscription in accordance
                    with Section 5 below.
                  </p>
                  <p>
                    You should also note that Section 13 of these Terms contains provisions governing how claims that
                    you and Soul Media Limited have against each other are resolved. In particular, it contains an
                    arbitration agreement that will, with limited exceptions, require disputes between us to be
                    submitted to binding and final arbitration. You have a right to opt out of the arbitration agreement
                    pursuant to Section 13 below. If you do not opt out of the arbitration agreement in accordance with
                    Section 13, (i) you will only be permitted to pursue claims and seek relief against us on an
                    individual basis only; and (ii) you are waiving your right to seek relief in a court of law and to
                    have a jury trial on your claims.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-10">
            {sections.map((section, si) => (
              <div key={si} id={section.id} className="bg-white rounded-3xl p-8 border border-gray-100 scroll-mt-32">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F5F0FF] flex-shrink-0">
                    <i className={`${section.icon} text-[#7C55CC] text-lg`}></i>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                </div>
                <div className="flex flex-col gap-6">
                  {section.custom1 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        Before you can use the App, you will need to register for an account (
                        <strong>&ldquo;Account&rdquo;</strong>). In order to create an Account you must:
                      </p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>
                          be at least 18 years old or the age of majority to legally enter into a contract under the
                          laws of your home country if that happens to be greater than 18; and
                        </li>
                        <li>be legally permitted to use the App by the laws of your home country.</li>
                      </ol>
                      <p>
                        Please note that we monitor for underage use and we will terminate, suspend or ask you to verify
                        your Account if we have reason to believe that you may be underage.
                      </p>
                      <p>
                        You can create an Account via manual registration, or by using your Apple ID or Google login
                        details. For more information about what information we use and how we use it, please check out
                        our{" "}
                        <Link to="/privacy-policy" className="underline font-semibold">
                          Privacy Policy
                        </Link>
                        .
                      </p>
                      <p>
                        Unfortunately, we cannot allow you to use another person&apos;s Aurays account or to share your
                        Aurays account with any other person without permission. You are responsible for ensuring that
                        any use of your account complies with these Terms.
                      </p>
                      <p>
                        You&apos;ll have great fun on Aurays, but if you feel the need to leave, you can delete your
                        Account at any time by going to the &lsquo;Settings&rsquo; page when you are logged in and
                        clicking on the &lsquo;Delete account&rsquo; link. Your Account will be deleted immediately but
                        it may take a little while for Your Content (defined below) to be completely removed from the
                        App. Your profile information will be treated in accordance with our Privacy Policy. If you
                        delete your Account and try to create a new account within this time period using the same
                        credentials, we will re-activate your Account for you.
                      </p>
                      <p>
                        We use a combination of automated systems, user reports and a team of moderators to monitor and
                        review accounts and content to identify breaches of these Terms. We reserve the right at our
                        sole discretion to terminate or suspend any Account, restrict access to the App, or make use of
                        any operational, technological, legal or other means available to enforce the Terms (including
                        without limitation blocking specific IP addresses). For users residing in the European Union (
                        <strong>&ldquo;EU&rdquo;</strong>), we will notify you when we take action against your Account
                        or Your Content (defined below) unless it is not appropriate for us to do so (for example, we
                        are not permitted by law enforcement agencies). For all other users, we may take such action, at
                        any time without liability and without the need to give you prior notice. Without limiting the
                        foregoing in any way and unless otherwise prohibited by mandatory laws in the country in which
                        you reside, we expressly reserve the right to terminate or suspend your Account without notice
                        (i) for violating these Terms, (ii) due to your conduct on the App, or your conduct with other
                        users of the App (including your &ldquo;offline&rdquo; conduct), if we, in our sole discretion,
                        determine your conduct was inappropriate or improper, (iii) if we or our affiliates, in our or
                        their sole discretion, determine your conduct on other apps operated by our affiliates was
                        inappropriate or improper, or (iv) for any reasons whatsoever that we deem, in our sole
                        discretion, justifies termination. If your Account is terminated or suspended, you agree you
                        will not receive a refund for any paid service or features you have already been charged for. If
                        you believe that we&apos;ve made a mistake in taking action on your Account or Your Content
                        (defined below), you have the option to appeal our decision within a six-month period. If you
                        wish to appeal a decision, please start by taking a moment to read the relevant section of our{" "}
                        <Link to="/community-guidelines" className="underline font-semibold">
                          Community Guidelines
                        </Link>
                        . If you wish to appeal a decision, please contact us.
                      </p>
                      <p>
                        You may not access, tamper with, or use non-public areas of the App or our systems. Certain
                        portions of the App may not be accessible if you have not registered for an Account.
                      </p>
                    </div>
                  ) : section.custom2 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>There are three types of content that you will be able to access on the App:</p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>
                          content that you upload and provide (<strong>&ldquo;Your Content&rdquo;</strong>);
                        </li>
                        <li>
                          content that members provide (<strong>&ldquo;Member Content&rdquo;</strong>); and
                        </li>
                        <li>
                          content that Aurays provides (including, without limitation, database(s) and/or software) (
                          <strong>&ldquo;Our Content&rdquo;</strong>).
                        </li>
                      </ol>
                      <p>
                        There is certain content we can&apos;t allow on Aurays. Our Community Guidelines form part of
                        these Terms and outline what content and conduct is accepted on and off our App. You agree to
                        comply with our Community Guidelines as may be updated from time to time.
                      </p>
                      <p>
                        We want our users to be able to express themselves as much as possible on Aurays, but we have to
                        impose restrictions on certain content which:
                      </p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>is illegal or encourages, promotes or incites any illegal activity;</li>
                        <li>is harmful to minors;</li>
                        <li>is defamatory or libellous;</li>
                        <li>
                          itself, or the posting of which, infringes any third party&apos;s rights (including, without
                          limitation, intellectual property rights and privacy rights);
                        </li>
                        <li>
                          shows another person which was created or distributed without that person&apos;s consent;
                        </li>
                        <li>
                          contains language or imagery which could be deemed offensive or is likely to harass, upset,
                          embarrass, alarm or annoy any other person;
                        </li>
                        <li>is obscene, pornographic, violent or otherwise may offend human dignity;</li>
                        <li>
                          is abusive, insulting or threatening, discriminatory or which promotes or encourages racism,
                          sexism, hatred or bigotry;
                        </li>
                        <li>
                          relates to commercial activities (including, without limitation, sales, competitions and
                          advertising, links to other websites or premium line telephone numbers);
                        </li>
                        <li>involves the transmission of &ldquo;junk&rdquo; mail or &ldquo;spam&rdquo;;</li>
                        <li>
                          impersonates or intends to deceive or manipulate a person (including, without limitation,
                          scams and inauthentic behaviour);
                        </li>
                        <li>
                          contains any spyware, adware, viruses, corrupt files, worm programs or other malicious code
                          designed to interrupt, damage or limit the functionality of or disrupt any software, hardware,
                          telecommunications, networks, servers or other equipment, Trojan horse or any other material
                          designed to damage, interfere with, wrongly intercept or expropriate any data or personal
                          information whether from Bumble or otherwise; or
                        </li>
                        <li>in any other way violates our Community Guidelines.</li>
                      </ul>
                      <p>
                        <strong className="text-gray-800">Your Content</strong>
                      </p>
                      <p>
                        You agree that Your Content must comply with our Community Guidelines as updated from time to
                        time. As Your Content is unique, you are responsible and liable for Your Content. You will
                        indemnify, defend, release, and hold us harmless from any claims made in connection with Your
                        Content.
                      </p>
                      <p>
                        You may not display any personal contact or banking information on your individual profile page
                        whether in relation to you or any other person (for example, names, home addresses or postcodes,
                        telephone numbers, email addresses, URLs, credit/debit card or other banking details). If you do
                        choose to reveal any personal information about yourself to other users, whether via email or
                        otherwise, it is at your own risk. We encourage you to use the same caution in disclosing
                        details about yourself to third parties online as you would under any other circumstances.
                      </p>
                      <p>
                        As Aurays is a public community, Your Content will be visible to other users of the App all
                        around the world - so make sure you are comfortable sharing Your Content before you post. As
                        such, you agree that Your Content may be viewed by other users and any person visiting,
                        participating in or who is sent a link to the App (e.g. individuals who receive a link to a
                        user&apos;s profile or shared content from other Aurays Users). By uploading Your Content on
                        Aurays, you represent and warrant to us that you have all necessary rights and licences to do
                        so, and automatically grant us a non-exclusive, royalty free, perpetual, worldwide licence to
                        use Your Content in any way (including, without limitation, editing, copying, modifying,
                        adapting, translating, reformatting, creating derivative works from, incorporating into other
                        works, advertising, distributing and otherwise making available to the general public such
                        Content, whether in whole or in part and in any format or medium currently known or developed in
                        the future).
                      </p>
                      <p>
                        <strong className="text-gray-800">Licensing and Content Protection</strong>
                      </p>
                      <p>
                        You agree that Soul Media Limited may assign and/or sub-license the non-exclusive license you
                        have granted us (to host and display Your Content) to our affiliates, partners, or successors in
                        the event of a merger, acquisition, or corporate restructuring, without requiring further
                        approval from you.
                      </p>
                      <p>
                        Aurays is a social platform and does not provide a data backup service. We have no obligation to
                        store or maintain Your Content indefinitely. We strongly recommend that you maintain your own
                        copies of any important photos or information posted to the App.
                      </p>
                      <p>
                        To prevent the unconsented use of Your Content by third parties outside of Aurays, you hereby
                        authorize Soul Media Limited to act on your behalf as your agent to identify and address
                        infringing or unauthorized uses of Your Content.
                      </p>
                      <p>
                        Takedown authority includes, but is not limited to, the right (but not the obligation) for Soul
                        Media Limited to send Notifications of Alleged Infringement or &ldquo;takedown notices&rdquo;
                        pursuant to the Copyright Ordinance (Cap. 528) of the laws of Hong Kong and the Code of Practice
                        for Online Service Providers. We may take such action if Your Content is scraped, copied, or
                        used by third parties outside of Aurays without your permission.
                      </p>
                      <p>
                        If Your Content is used by third parties in a manner that constitutes &ldquo;doxxing&rdquo; or a
                        breach of the Personal Data (Privacy) Ordinance (Cap. 486), you authorize us to cooperate with
                        the Office of the Privacy Commissioner for Personal Data (PCPD) to protect your rights. So that
                        we can prevent the unconsented use of Your Content by other members or third parties outside of
                        Aurays, you authorise us to act on your behalf with respect to such infringing and/or
                        unauthorised uses. This expressly includes the authority, but not the obligation, for us to send
                        takedown notices on your behalf if Your Content is taken and used by third parties outside of
                        Aurays.
                      </p>
                      <p>
                        <strong className="text-gray-800">Member Content</strong>
                      </p>
                      <p>
                        Other members of Aurays will also share content via the App. Member Content belongs to the user
                        who posted the content and is stored on our servers and displayed via the App at the direction
                        of the user providing the Member Content.
                      </p>
                      <p>
                        You do not have any rights in relation to other users&apos; Member Content. You may only use
                        other Aurays users&apos; personal information to the extent that your use matches the App&apos;s
                        core purpose of facilitating intentional social and dating connections.
                      </p>
                      <p>
                        You are strictly prohibited from using other users&apos; information for commercial purposes,
                        spamming, harassment, stalking, or making unlawful threats. Please note that the unauthorized
                        disclosure of other users&apos; personal data with intent to cause harm (
                        <strong>&ldquo;Doxxing&rdquo;</strong>) is a criminal offense under the Personal Data (Privacy)
                        Ordinance (Cap. 486) of the laws of Hong Kong. We reserve the right to terminate your Account
                        and report such activity to the Office of the Privacy Commissioner for Personal Data (PCPD) or
                        law enforcement.
                      </p>
                      <p>
                        Member Content is subject to the Copyright Ordinance (Cap. 528). Soul Media Limited operates as
                        an Online Service Provider (OSP) and adheres to the Code of Practice for Online Service
                        Providers regarding copyright infringement.
                      </p>
                      <p>
                        If you believe any Member Content infringes upon your intellectual property rights or violates
                        our Community Guidelines, please submit a formal Notice of Alleged Infringement to our
                        designated agent at support@aurays.com. Upon receipt of a valid notice, we will act
                        expeditiously to remove or disable access to the infringing material in accordance with the
                        statutory &ldquo;Safe Harbour&rdquo; provisions of Hong Kong law.
                      </p>
                      <p>
                        <strong className="text-gray-800">Our Content</strong>
                      </p>
                      <p>
                        You may be wondering what happens to the rest of the Content on Aurays. Well, it belongs to us!
                        Any other text, content, graphics, user interfaces, trademarks, logos, sounds, artwork, and
                        other intellectual property appearing on Aurays, as well as the Aurays software and database(s),
                        are owned, controlled or licensed by Soul Media Limited and are protected by copyright,
                        trademark, data, database rights and/or other intellectual property law rights. All right, title
                        and interest in and to Our Content remains with us at all times.
                      </p>
                      <p>
                        We grant you a non-exclusive, limited, personal, non-transferable, revocable, licence to access
                        and use Our Content, without the right to sublicense, under the following conditions:
                      </p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>
                          you shall not use, sell, modify, or distribute Our Content except as permitted by the
                          functionality of the App;
                        </li>
                        <li>you shall not use our name in metatags, keywords and/or hidden text;</li>
                        <li>
                          you shall not create derivative works from Our Content or scrape, disable, decompile, analyse
                          or in any way commercially exploit Our Content, in whole or in part, in any way; and
                        </li>
                        <li>you shall use Our Content for lawful purposes only.</li>
                      </ul>
                      <p>We reserve all other rights.</p>
                      <p>
                        <strong className="text-gray-800">No Obligation to Pre-Screen Content</strong>
                      </p>
                      <p>
                        While we don&apos;t assume any obligation to pre-screen any of Your Content or any Member
                        Content, there may be times where we need to step in to help keep our members safe, and we
                        reserve the right to review, pre-screen, refuse and/or remove any Member Content and Your
                        Content, including content exchanged between users in direct messages as set out in these Terms.
                      </p>
                      <p>
                        <strong className="text-gray-800">Use of Recommender Systems</strong>
                      </p>
                      <p>
                        We have developed matching algorithms to predict your compatibility with other users and so we
                        can show you people we think are a good match for you. You can learn more about our use of
                        recommender systems and the main parameters we use in our Privacy Policy.
                      </p>
                    </div>
                  ) : section.custom3 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>You agree to:</p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>
                          comply with all applicable laws, including without limitation, privacy laws, intellectual
                          property laws, anti-spam laws, equal opportunity laws and regulatory requirements;
                        </li>
                        <li>
                          use your real name and real age in creating your Bumble account and on your profile; and
                        </li>
                        <li>
                          use the services in a safe, inclusive and respectful manner and adhere to our Community
                          Guidelines at all times.
                        </li>
                      </ul>
                      <p>You agree that you will not:</p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>
                          act in an unlawful or disrespectful manner including being dishonest, abusive or
                          discriminatory;
                        </li>
                        <li>
                          misrepresent your identity, your age, your current or previous positions, qualifications or
                          affiliations with a person or entity;
                        </li>
                        <li>disclose information that you do not have the consent to disclose;</li>
                        <li>stalk or harass any other user of the App;</li>
                        <li>
                          use the App in any deceptive, inauthentic or manipulative way, including engaging in conduct
                          or distributing content relating to scams, spam, inauthentic profiles or commercial and
                          promotional activity;
                        </li>
                        <li>submit appeals, reports, notices or complaints that are manifestly unfounded; or;</li>
                        <li>
                          develop, support or use software, devices, scripts, robots, other types of mobile code or any
                          other means or processes (including crawlers, browser plugins and add-on or other technology)
                          to scrape or otherwise exfiltrate from Aurays or its services, or otherwise copy profiles and
                          other data from the services.
                        </li>
                      </ul>
                      <p>
                        We don&apos;t like users misbehaving in the Aurays community. You can report any abuse or
                        complain about Member Content by contacting us, outlining the abuse and/or complaint. You can
                        also report a user directly from a profile or in chat by clicking the &lsquo;Block &
                        Report&rsquo; link. We reserve the right to investigate any possible violations of these Terms,
                        any Aurays user&apos;s rights, or any third party rights and we may, in our sole discretion,
                        immediately terminate any user&apos;s right to use of the App without prior notice, as set out
                        further in Section 1 above, and/or remove any improper, infringing or otherwise unauthorised
                        Member Content submitted to the App.
                      </p>
                      <p>
                        We don&apos;t control any of the things our users say or do, so you are solely responsible for
                        your interactions with other users of the App.
                      </p>
                      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-medium text-gray-700 uppercase">
                        <p>
                          IN CERTAIN CIRCUMSTANCES, SUCH AS IN RESPONSE TO MEMBER-GENERATED OR PRESS REPORTS OF
                          SUSPECTED MISCONDUCT, SOUL MEDIA LIMITED MAY INVESTIGATE WHETHER A MEMBER HAS A CRIMINAL
                          HISTORY, WHICH MAY, DEPENDING ON THE CIRCUMSTANCES, INCLUDE SEARCHING SEX OFFENDER REGISTRIES
                          OR OTHER PUBLIC RECORDS. IF SUCH INVESTIGATIONS IDENTIFY A PERSON WITH A CRIMINAL CONVICTION,
                          SOUL MEDIA LIMITED MAY USE AVAILABLE INFORMATION TO DETERMINE WHETHER THE PERSON IDENTIFIED BY
                          THE INVESTIGATION IS THE SAME PERSON AS THE MEMBER ABOUT WHOM THE INVESTIGATION WAS CONDUCTED.
                          FOLLOWING ANY SUCH INVESTIGATION, WITH RESPECT TO ANY MEMBER THAT SOUL MEDIA LIMITED
                          REASONABLY BELIEVES TO HAVE BEEN CONVICTED OF A SEX OFFENCE (SUCH AS SEXUAL ASSAULT OR SEXUAL
                          HARASSMENT, AND INCLUDING ANY REGISTERED SEX OFFENDERS), OR A CONVICTION FOR HUMAN
                          TRAFFICKING, STALKING, KIDNAPPING, CHILD ABUSE, DOMESTIC ABUSE, MURDER, HATE CRIMES, OR
                          TERRORISM OR VIOLENT EXTREMISM, SOUL MEDIA LIMITED MAY TAKE STEPS TO BLOCK THAT PERSON FROM
                          USING THE SERVICE AND COMMUNICATING WITH OTHER MEMBERS OVER THE PLATFORM. SOUL MEDIA LIMITED
                          RESERVES THE RIGHT TO BLOCK MEMBERS FOR OTHER OFFENCES, OR FOR OTHER VIOLATIONS OF THESE
                          TERMS, OR FOR OTHER REASONS IN THEIR SOLE DISCRETION. CRIMINAL BACKGROUND INVESTIGATIONS, WHEN
                          CONDUCTED, ARE NOT TYPICALLY UPDATED BY SOUL MEDIA LIMITED. ADDITIONALLY, WHILE INVESTIGATIONS
                          FOR CRIMINAL HISTORIES MAY BE CONDUCTED ON SOME MEMBERS, THEY ARE NOT FOOLPROOF AND MOST
                          MEMBERS ARE NOT EXPECTED TO BE SUBJECT TO ANY FORM OF CHECK FOR CRIMINAL HISTORIES. ANY
                          INVESTIGATIONS FOR CRIMINAL HISTORIES MAY GIVE MEMBERS A FALSE SENSE OF SECURITY. THE CHECKS
                          FOR CRIMINAL HISTORIES THAT MAY BE CONDUCTED BY SOUL MEDIA LIMITED ARE NOT A PERFECT SAFETY
                          SOLUTION. CRIMINALS MAY CIRCUMVENT EVEN THE MOST SOPHISTICATED SEARCH TECHNOLOGY. NOT ALL
                          CRIMINAL RECORDS ARE PUBLIC AND NOT ALL DATABASES ARE UP TO DATE. ONLY PUBLICLY AVAILABLE
                          CONVICTIONS ARE INCLUDED IN CHECKS, AND CHECKS DO NOT COVER OTHER TYPES OF CONVICTIONS OR
                          ARRESTS.
                        </p>
                      </div>
                      <p>
                        You agree to, and hereby do, release Soul Media Limited and its successors from any claims,
                        demands, losses, damages, rights, and actions of any kind, including personal injuries, death
                        and property damage, that either directly or indirectly arises from your interactions with or
                        conduct of other users of the App.
                      </p>
                      <p>
                        Scraping or replicating any part of the App without our prior consent is expressly prohibited.
                        This includes by any means (automated or otherwise) other than through our currently available,
                        published interfaces - unless you have been specifically allowed to do so in a separate
                        agreement with us.
                      </p>
                      <p>
                        <strong className="text-gray-800">Background Check</strong>
                      </p>
                      <p>
                        In certain circumstances, such as in response to member-generated or press reports of suspected
                        misconduct, Soul Media Limited may investigate whether a member has a criminal history, which
                        may, depending on the circumstances, include searching sex offender registries or other public
                        records. If such investigations identify a person with a criminal conviction, Soul Media Limited
                        may use available information to determine whether the person identified by the investigation is
                        the same person as the member about whom the investigation was conducted. Following any such
                        investigation, with respect to any member that Soul Media Limited reasonably believes to have
                        been convicted of a sex offense (such as sexual assault or sexual harassment, and including any
                        registered sex offenders), or a conviction for human trafficking, stalking, kidnapping, child
                        abuse, domestic abuse, murder, hate crimes, or terrorism or violent extremism, Soul Media
                        Limited may take steps to block that person from using the service and communicating with other
                        members over the platform. Soul Media Limited reserves the right to block members for other
                        offenses, or for other violations of these terms, or for other reasons in their sole discretion.
                        Criminal background investigations, when conducted, are not typically updated by Soul Media
                        Limited. Additionally, while investigations for criminal histories may be conducted on some
                        members, they are not foolproof and most members are not expected to be subject to any form of
                        check for criminal histories. Any investigations for criminal histories may give members a false
                        sense of security. The checks for criminal histories that may be conducted by Soul Media Limited
                        are not a perfect safety solution. Criminals may circumvent even the most sophisticated search
                        technology. Not all criminal records are public in all states and not all databases are up to
                        date. Only publicly available convictions are included in checks, and checks do not cover other
                        types of convictions or arrests.
                      </p>
                      <p>
                        <strong className="text-gray-800">Identification and Device Verification</strong>
                      </p>
                      <p>
                        To use our service, you must be at least 18 years old or the age of majority to legally enter
                        into a contract under the laws of your home country if that happens to be greater than 18.
                        Please note that we monitor for underage use and we will terminate, suspend or ask you to verify
                        your Account if we have reason to believe that you may be underage.
                      </p>
                      <p>
                        We may require users to verify their identity in order to register an Account, or to continue
                        using an Account, at any time. We also reserve the right to require users to provide a valid
                        phone number registered to their Account and to verify it at any time. Please note that if you
                        don&apos;t complete a verification request, you may be unable to register an Account or you may
                        lose access to the Aurays services.
                      </p>
                      <p>We may also suspend or close your Account where you have multiple Accounts.</p>
                      <p>
                        Aurays may have to contact user Mobile Network Operator in order to validate telephone numbers,
                        verify identity and carry out checks for the prevention and detection of crime including fraud
                        and/or money laundering.
                      </p>
                      <p>
                        <strong className="text-gray-800">When a member may be suspended or blocked</strong>
                      </p>
                      <p>
                        All members must comply with the platform rules described and referenced in our Community
                        Guidelines. If you behave in a way that goes against Aurays&apos;s Community Guidelines, values,
                        or otherwise act in any way we believe to be potentially harmful to Aurays or its members, we
                        may take a range of actions on your account. When determining the penalty for violating our
                        community guidelines, we consider a number of factors.
                      </p>
                      <p>We may:</p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>Remove the content</li>
                        <li>Issue a warning</li>
                        <li>Ban the offending member from some or all Soul Media Limited apps</li>
                      </ul>
                      <p>
                        When necessary, we also may cooperate with law enforcement to assist in potential criminal
                        investigations related to member conduct.
                      </p>
                      <p>
                        Your treatment towards others outside of the Aurays app can also result in action against your
                        account. If we&apos;re made aware of harm between members on dates, meetups with friends, via
                        text message or direct messaging platforms, or relevant alleged criminal or harmful conduct
                        committed in your past or outside of Aurays, we may take action as if it happened on our
                        platform.
                      </p>
                      <p>
                        You cannot submit appeals, reports, notices or complaints that are false reports or report for
                        malicious, biased, or other illegitimate reasons.
                      </p>
                      <p>
                        <strong className="text-gray-800">Appeals rights and appeal process</strong>
                      </p>
                      <p>
                        If you believe that we&apos;ve made a mistake in taking action on your Account or Your Content,
                        you have the option to appeal our decision within a six-month period. If you wish to appeal a
                        decision, please start by taking a moment to read the relevant section of our{" "}
                        <Link to="/community-guidelines" className="underline font-semibold">
                          Community Guidelines
                        </Link>
                        . If you still think there has been a mistake in the action taken on your account or content,
                        the best way to appeal is to fill out the appeals form linked in the notification shown to you
                        within the app at the time. Alternatively, you can contact us (please explain why you are
                        appealing our decision, and give as much detail as you can).
                      </p>
                      <p>
                        <strong className="text-gray-800">User Notice</strong>
                      </p>
                      <p>
                        When you submit a Block & Report, a member of the support team will review it and take action
                        based on our Community Guidelines, which can include a warning or banning them from the app.
                        Aurays will not tell the person who you reported that you reported them.
                      </p>
                      <p>
                        <strong className="text-gray-800">Notice regarding Sexual Conduct</strong>
                      </p>
                      <p>
                        Consent is required at all times. Everyone on Aurays is expected to discuss and respect
                        boundaries. This includes never making assumptions about what someone is comfortable with.
                        Consent can be withdrawn at any time, and sex is never owed to anyone and sex without consent is
                        against the law and may result in criminal or civil liability.
                      </p>
                      <p>
                        <strong className="text-gray-800">Safety Measures</strong>
                      </p>
                      <p>
                        Safety is a top priority at Aurays. We use a combination of human moderators and automated
                        systems to monitor and review Aurays accounts and interactions for content that may be against
                        our Community Guidelines, against our Terms and Conditions, or otherwise harmful.
                      </p>
                      <p>
                        Our members play a critical role in the safety of Aurays by reporting content or behavior that
                        may violate our Community Guidelines. If anything happens that makes you feel uncomfortable or
                        unsafe, we highly encourage you to Unmatch with – or Block & Report – the member. See this
                        article for more info on what happens when you report something to Aurays.
                      </p>
                      <p>
                        For a full list of our safety resources and measures please visit our Safety Centre and our
                        safety handbook.
                      </p>
                    </div>
                  ) : section.custom5 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        Aurays may offer products and services for purchase on the App (
                        <strong>&ldquo;In-App Purchase&rdquo;</strong>). If you choose to make an In-App Purchase, you
                        acknowledge and agree that additional terms, disclosed to you at the point of purchase, may
                        apply, and that such additional terms are incorporated herein by reference..
                      </p>
                      <p>
                        You may make an In-App Purchase through the following payment methods (
                        <strong>&ldquo;Payment Method&rdquo;</strong>): (a) making a purchase through a third-party
                        platform such as the Apple App (<strong>&ldquo;Third Party Store&rdquo;</strong>), or (b) paying
                        with your credit card, debit card, PayPal account, PayMe, or AlipayHK, which will be processed
                        by a third-party processor. Once you have made an In-App Purchase, you authorise us to charge
                        your chosen Payment Method. If payment is not received by us from your chosen Payment Method,
                        you agree to promptly pay all amounts due upon demand by us.
                      </p>
                      <p>
                        <strong className="text-gray-800">Subscriptions and Auto-Renewal</strong>
                      </p>
                      <p>
                        Aurays may offer some services as automatically-renewing subscriptions, e.g., a one-week
                        subscription, one-month subscription, or three-month subscription (
                        <strong>&ldquo;Premium Services&rdquo;</strong>). IF YOU PURCHASE AN AUTOMATICALLY RENEWING
                        SUBSCRIPTION, YOUR SUBSCRIPTION WILL RENEW AT THE END OF THE PERIOD, UNLESS YOU CANCEL, AT
                        AURAYS&apos;S THEN-CURRENT PRICE FOR SUCH SUBSCRIPTIONS. To avoid charges for a new subscription
                        period, you must cancel, as described below, before the end of the then-current subscription
                        period. Deleting your account or deleting the application from your device does not cancel your
                        subscription. You will be given notice of changes in the pricing of the Premium Services to
                        which you have subscribed and an opportunity to cancel. If Aurays changes these prices and you
                        do not cancel your subscription, you agree that you will be charged at Aurays&apos;s
                        then-current pricing for subscription.
                      </p>
                      <p>
                        <strong className="text-gray-800">Cancelling Subscriptions</strong>
                      </p>
                      <p>
                        If you purchased a subscription directly from Aurays, you may cancel or change your Payment
                        Method via the payment settings option under your profile. If you purchased a subscription
                        through a Third Party Store, such as the Apple App Store, you will need to access your account
                        with that Third Party Store and follow instructions to change or cancel your subscription. If
                        you cancel your subscription, you may use your subscription until the end of the period you last
                        paid for, but (i) you will not (except as set forth in the subsection entitled
                        &ldquo;Refunds&rdquo; below) be eligible for a prorated refund, (ii) your subscription will not
                        be renewed when that period expires and (iii) you will then no longer be able to use the Premium
                        Services or In-App Purchases enabled by your subscription.
                      </p>
                      <p>
                        Because our Services may be utilised without a subscription, cancelling your subscription does
                        not remove your profile from our Services. If you wish to fully terminate your account, you must
                        terminate your account as set forth in Section 14.
                      </p>
                      <p>
                        Our pricing varies by a number of factors. We frequently offer promotional rates - which can
                        vary based on region, length of subscription, bundle size and more. We also regularly test new
                        features and payment options. Soul Media Limited reserves the right, including without prior
                        notice, to limit the available quantity of or discontinue making available any product, feature,
                        service or other offering; to impose conditions on the honouring of any coupon, discount, offer
                        or other promotion; to bar any user from making any transaction; and to refuse to provide any
                        user with any product, service or other offering or to honour any offer.
                      </p>
                      <p>
                        <strong className="text-gray-800">Free Trials</strong>
                      </p>
                      <p>
                        If you sign up for a free trial and do not cancel, your trial may convert into a paid
                        subscription and your Payment Method will be charged at the then-current price for such
                        subscription. Once your free trial converts to a paid subscription, your paid subscription will
                        continue to automatically renew at the end of each period, and your Payment Method will be
                        charged, until you cancel. To avoid charges for a new subscription period, you must cancel
                        before the end of the then-current subscription period or free trial period as described above.
                        Deleting your account or deleting the application from your device does not cancel your free
                        trial. If you have signed up for a free trial on Bumble through the Apple Store previously, you
                        will not be eligible for another free trial and you will then be automatically signed up to a
                        subscription and charged as described in this paragraph.
                      </p>
                      <p>
                        <strong className="text-gray-800">Refunds</strong>
                      </p>
                      <p>
                        Generally, all charges for purchases are nonrefundable, and there are no refunds or credits for
                        partially used periods.
                      </p>
                      <p>
                        Except as otherwise explicitly provided in these Terms or as required under the applicable laws
                        of Hong Kong (including the Supply of Services (Implied Terms) Ordinance, Cap. 457), all
                        purchases and subscriptions made on the App are final and non-refundable. If you purchased a
                        subscription through a third-party platform such as the Apple App, any requests for refunds must
                        be submitted directly to that platform provider and will be subject to their respective refund
                        policies. There are no refunds for subscription renewals. Blocked members are not entitled to
                        refunds.
                      </p>
                      <p>
                        <strong className="text-gray-800">Taxes</strong>
                      </p>
                      <p>
                        The payments required under this Section 5 do not include any Sales Tax that may be due in
                        connection with the Premium Services provided under these Terms. If Bumble determines it has a
                        legal obligation to collect a Sales Tax from you in connection with these Terms, Bumble will
                        collect such Sales Tax in addition to the payments required under this Section 5. If any Premium
                        Services, or payments for any Premium Services, under these Terms are subject to any Sales Tax
                        in any jurisdiction and you have not remitted the applicable Sales Tax to Aurays, you will be
                        responsible for the payment of such Sales Tax and any related penalties or interest to the
                        relevant tax authority. As used herein, &ldquo;Sales Tax&rdquo; means any sales or use tax and
                        any other tax measured by sales proceeds that is the functional equivalent of a sales tax where
                        the applicable taxing jurisdiction does not otherwise impose a sales or use tax.
                      </p>
                    </div>
                  ) : section.custom6 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        At times, you may be able to purchase a limited, personal, non-transferable, non-sublicensable,
                        revocable license to access special limited-use features from Aurays, referred to here as
                        &ldquo;Virtual Items.&rdquo; You can only purchase Virtual Items through Aurays or Aurays&apos;s
                        partners. Virtual Items represent a limited license right governed by this Agreement, and,
                        except as otherwise prohibited by applicable law, no title or ownership in or to Virtual Items
                        is being transferred or assigned to you. This Agreement, and your purchase of Virtual Items,
                        does not constitute the sale of any rights in Virtual Items.
                      </p>
                      <p>
                        Any Virtual Item balance shown in your account does not constitute a real-world balance or
                        reflect any stored value, but instead shows the extent of your licence to access Virtual Items.
                        Virtual Items do not incur fees for non-use. Your licence in Virtual Items will terminate on the
                        earlier of Aurays ceasing provision of services or your account closing or terminating. Aurays
                        may also at times provide Virtual Items as bonuses to, or parts of, paid subscriptions for its
                        services. Your ability to use Virtual Items you have access to in this manner may terminate at
                        the end of each of your subscription periods and your access to Virtual Items may not
                        &ldquo;roll over&rdquo; or accumulate through additional subscription periods. Your access to
                        Virtual Items gained through subscriptions may also end if you cancel your subscription.
                      </p>
                      <p>
                        Aurays, in its sole discretion, reserves the right to charge fees for the right to access or use
                        Virtual Items and/or may distribute Virtual Items with or without charge. Aurays may manage,
                        regulate, control, modify, or eliminate Virtual Items at any time, including taking actions that
                        may impact the perceived value or purchase price, if applicable, of any Virtual Items and Aurays
                        shall have no liability to you for doing so. You shall not sell, redeem, or otherwise transfer
                        Virtual Items to any person or entity. Virtual Items may only be redeemed through our Services.
                      </p>
                      <p>
                        ALL PURCHASES AND REDEMPTIONS OF VIRTUAL ITEMS MADE THROUGH OUR SERVICES ARE FINAL AND
                        NON-REFUNDABLE. YOU ACKNOWLEDGE THAT AURAYS IS NOT REQUIRED TO PROVIDE A REFUND FOR ANY REASON,
                        AND THAT YOU WILL NOT RECEIVE MONEY OR OTHER COMPENSATION FOR UNUSED VIRTUAL ITEMS WHEN AN
                        ACCOUNT IS CLOSED, WHETHER SUCH CLOSURE WAS VOLUNTARY OR INVOLUNTARY.
                      </p>
                    </div>
                  ) : section.custom7 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        We may provide you with emails, text messages, push notifications, alerts and other messages
                        related to the App and/or the Aurays services, such as enhancements, offers, products, events,
                        and other promotions. After downloading the App, you will be asked to accept or deny push
                        notifications/alerts. If you deny, you will not receive any push notifications/alerts. If you
                        accept, push notifications/alerts will be automatically sent to you. If you no longer wish to
                        receive push notifications/alerts from the App, you may opt out by changing your notification
                        settings on your mobile device. With respect to other types of messaging or communications, such
                        as emails, text messages, etc., you can unsubscribe or opt out by either following the specific
                        instructions included in such communications, or by emailing us with your request at
                        support@aurays.app.
                      </p>
                      <p>
                        The App may allow access to or make available opportunities for you to view certain content and
                        receive other products, services and/or other materials based on your location. To make these
                        opportunities available to you, the App will determine your location using one or more reference
                        points, such as GPS, Bluetooth and/or software within your mobile device. If you have set your
                        mobile device to disable GPS, Bluetooth or other location determining software or do not
                        authorise the App to access your location data, you will not be able to access such
                        location-specific content, products, services and materials. For more about how the App uses and
                        retains your information, please read the Privacy Policy.
                      </p>
                    </div>
                  ) : section.custom8 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-700 leading-relaxed uppercase font-medium bg-gray-50 p-6 rounded-2xl border border-gray-100">
                      <p>
                        THE APP, SITE, OUR CONTENT, AND MEMBER CONTENT ARE ALL PROVIDED TO YOU &ldquo;AS IS&rdquo; AND
                        &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING
                        BUT NOT LIMITED TO, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT. WITHOUT
                        LIMITING THE FOREGOING, WE DO NOT GUARANTEE THE COMPATIBILITY OF ANY MATCHES.
                      </p>
                      <p>
                        SHOULD APPLICABLE LAW NOT PERMIT THE FOREGOING EXCLUSION OF EXPRESS OR IMPLIED WARRANTIES, THEN
                        WE GRANT THE MINIMUM EXPRESS OR IMPLIED WARRANTY REQUIRED BY APPLICABLE LAW. NO ADVICE OR
                        INFORMATION, WHETHER ORAL OR WRITTEN, SHALL CREATE ANY WARRANTY, REPRESENTATION OR GUARANTEE NOT
                        EXPRESSLY STATED IN THIS SECTION.
                      </p>
                      <p>
                        ADDITIONALLY, WE DO NOT MAKE ANY WARRANTIES THAT THE APP OR SITE WILL BE UNINTERRUPTED, SECURE
                        OR ERROR FREE OR THAT YOUR USE OF THE APP OR SITE WILL MEET YOUR EXPECTATIONS, OR THAT THE APP,
                        SITE, OUR CONTENT, ANY MEMBER CONTENT, OR ANY PORTION THEREOF, IS CORRECT, ACCURATE, OR
                        RELIABLE. YOUR USE OF THE APP OR SITE IS AT YOUR OWN RISK. YOU ARE SOLELY RESPONSIBLE FOR YOUR
                        INTERACTIONS WITH OTHER MEMBERS. SOUL MEDIA LIMITED IS NOT RESPONSIBLE FOR THE CONDUCT OF ANY
                        USER. SOUL MEDIA LIMITED DOES NOT CONDUCT CRIMINAL BACKGROUND CHECKS ON ITS MEMBERS.
                      </p>
                    </div>
                  ) : section.custom9 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-700 leading-relaxed uppercase font-medium bg-gray-50 p-6 rounded-2xl border border-gray-100">
                      <p>
                        NEITHER US NOR ANY OWNER WILL BE LIABLE FOR ANY DAMAGES, DIRECT, INDIRECT, INCIDENTAL,
                        CONSEQUENTIAL, SPECIAL, OR PUNITIVE, INCLUDING, WITHOUT LIMITATION, LOSS OF DATA, INCOME, PROFIT
                        OR GOODWILL, LOSS OF OR DAMAGE TO PROPERTY AND CLAIMS OF THIRD PARTIES ARISING OUT OF YOUR
                        ACCESS TO OR USE OF THE APP, SITE, OUR CONTENT, OR ANY MEMBER CONTENT, HOWEVER CAUSED, WHETHER
                        BASED ON BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), PROPRIETARY RIGHTS INFRINGEMENT,
                        PRODUCT LIABILITY OR OTHERWISE.
                      </p>
                      <p>
                        THE FOREGOING SHALL APPLY EVEN IF WE WERE ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IF YOU
                        BECOME DISSATISFIED IN ANY WAY WITH THE APP OR SITE, YOUR SOLE AND EXCLUSIVE REMEDY IS TO STOP
                        YOUR USE OF THE APP AND SITE.
                      </p>
                      <p>
                        YOU HEREBY WAIVE ANY AND ALL CLAIMS ARISING OUT OF YOUR USE OF THE APP OR SITE. IF ANY PORTION
                        OF THIS LIMITATION ON LIABILITY IS FOUND TO BE INVALID OR UNENFORCEABLE FOR ANY REASON, THEN OUR
                        AGGREGATE LIABILITY SHALL NOT EXCEED ONE THOUSAND HONG KONG DOLLARS (HK$1,000).
                      </p>
                      <p>
                        THE LIMITATION OF LIABILITY HEREIN IS A FUNDAMENTAL ELEMENT OF THE BASIS OF THE BARGAIN AND
                        REFLECTS A FAIR ALLOCATION OF RISK. THE APP AND SITE WOULD NOT BE PROVIDED WITHOUT SUCH
                        LIMITATIONS AND YOU AGREE THAT THE LIMITATIONS AND EXCLUSIONS OF LIABILITY, DISCLAIMERS AND
                        EXCLUSIVE REMEDIES SPECIFIED HEREIN WILL SURVIVE EVEN IF FOUND TO HAVE FAILED IN THEIR ESSENTIAL
                        PURPOSE. THE FOREGOING DOES NOT APPLY TO LIABILITY ARISING FROM ANY FRAUD OR FRAUDULENT
                        MISREPRESENTATIONS, OR ANY OTHER LIABILITY THAT CANNOT BE LIMITED BY APPLICABLE LAW.
                      </p>
                    </div>
                  ) : section.custom10 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        All the actions you make and information you post on the App remain your responsibility.
                        Therefore, you agree to indemnify, defend, release, and hold us, and our partners, licensors,
                        affiliates, contractors, officers, directors, employees, representatives and agents, harmless,
                        from and against any third party claims, damages (actual and/or consequential), actions,
                        proceedings, demands, losses, liabilities, costs and expenses (including reasonable legal fees)
                        suffered or reasonably incurred by us arising as a result of, or in connection with:
                      </p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>any negligent acts, omissions or wilful misconduct by you;</li>
                        <li>your access to and use of the App;</li>
                        <li>the uploading or submission of Content to the App by you;</li>
                        <li>any breach of these Terms by you; and/or</li>
                        <li>your violation of any law or of any rights of any third party.</li>
                      </ol>
                      <p>
                        We retain the exclusive right to settle, compromise and pay any and all claims or causes of
                        action which are brought against us without your prior consent. If we ask, you will co-operate
                        fully and reasonably as required by us in the defence of any relevant claim.
                      </p>
                      <p>
                        Notwithstanding anything to the contrary in these Terms, nothing in this indemnity section shall
                        require you to indemnify or hold harmless the Company for any loss, damage, or legal expense
                        resulting directly from the Company&apos;s own fraud, willful misconduct, gross negligence, or
                        intentional misrepresentation.
                      </p>
                    </div>
                  ) : section.custom11 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        If you believe any content on the App infringes a copyright that you own or are authorized to
                        represent, please submit a written notification of alleged infringement (
                        <strong>&ldquo;Copyright Infringement Notice&rdquo;</strong>) to the Company&apos;s Designated
                        Agent pursuant to{" "}
                        <strong>Section 88C of the Copyright Ordinance (Cap. 528, Laws of Hong Kong)</strong>. The
                        Copyright Infringement Notice must include the following information:
                      </p>
                      <ul className="flex flex-col gap-2 pl-4 list-disc">
                        <li>
                          <strong>Particulars of the Complainant:</strong> The name, contact telephone number, and an
                          address for service in Hong Kong of the individual or business furnishing the notice;
                        </li>
                        <li>
                          <strong>Signature:</strong> A physical or electronic signature of the person authorized to act
                          on behalf of the owner of the exclusive copyright that is allegedly infringed;
                        </li>
                        <li>
                          <strong>Identification of the Work:</strong> Clear identification of the copyrighted work
                          claimed to have been infringed;
                        </li>
                        <li>
                          <strong>Identification of Infringing Material:</strong> Description of the material claimed to
                          be infringing and information reasonably sufficient to permit the Company to locate and
                          identify the specific electronic location of that material on the App (such as a profile URL
                          or screenshot);
                        </li>
                        <li>
                          <strong>Statement of Unauthorized Use:</strong> A statement that you have a good faith belief
                          that the use of the material in the manner complained of is not authorized by the copyright
                          owner, its agent, or the law; and
                        </li>
                        <li>
                          <strong>Statement of Accuracy:</strong> A statement confirming that the information in the
                          notification is true, accurate, and correct to the best of your knowledge.
                        </li>
                      </ul>
                      <p>
                        Copyright Infringement Notices should be sent to Soul Media Limited&apos;s Designated Agent by
                        email to{" "}
                        <a href="mailto:support@aurays.app" className="text-[#7C55CC] font-semibold">
                          support@aurays.app
                        </a>
                        .
                      </p>
                    </div>
                  ) : section.custom12 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        The following additional terms and conditions apply to you if you download the App from a Third
                        Party Store. To the extent that the other terms and conditions of these Terms are less
                        restrictive than, or otherwise conflict with, the terms and conditions of this Section, the more
                        restrictive or conflicting terms and conditions in this Section will apply, but solely with
                        respect to the App and the Third Party Store. You acknowledge and agree that:
                      </p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>
                          These Terms are concluded solely between you and Soul Media Limited and not with the providers
                          of the Third Party Store, and Soul Media Limited (and not the Third Party Store providers) is
                          solely responsible for the App and the content thereof. To the extent that these Terms provide
                          for usage rules for the App which are less restrictive or in conflict with the applicable
                          terms of service of the Third Party Store from which you obtain the App, the more restrictive
                          or conflicting term of the Third Party Store will take precedence and will apply.
                        </li>
                        <li>
                          The Third Party Store provider has no obligation whatsoever to provide any maintenance and
                          support services with respect to the App. Soul Media Limited is solely responsible for any
                          product warranties, whether express or implied by law, to the extent not effectively
                          disclaimed. The Third Party Store provider will have no warranty obligation whatsoever with
                          respect to the App, and any other claims, losses, liabilities, damages, costs or expenses
                          attributable to any failure to conform to any warranty will be the sole responsibility of Soul
                          Media Limited.
                        </li>
                        <li>
                          Soul Media Limited, not the Third Party Store provider, is responsible for addressing any
                          claims you or any third party may have relating to the App or your possession and/or use of
                          the App, including, but not limited to: (i) product liability claims; (ii) any claim that the
                          App fails to conform to any applicable legal or regulatory requirement; (iii) claims arising
                          under consumer protection or similar legislation; and/or (iv) intellectual property
                          infringement claims.
                        </li>
                        <li>
                          The Third Party Store provider and its subsidiaries are third party beneficiaries of these
                          Terms, and, upon your acceptance of these Terms, the Third Party Store provider from whom you
                          obtained the App will have the right (and will be deemed to have accepted the right) to
                          enforce these Terms against you as a third party beneficiary thereof.
                        </li>
                      </ol>
                      <p>
                        In the event of a conflict between a Third Party Store&apos;s or mobile carrier&apos;s
                        applicable terms and conditions and these Terms, the terms and conditions of the Third Party
                        Store or mobile carrier shall govern and control. We are not responsible and have no liability
                        whatsoever for third-party goods or services you obtain through a Third Party Store or mobile
                        carrier. We encourage you to make whatever investigation you feel necessary or appropriate
                        before proceeding with any online transaction with any of these third parties.
                      </p>
                    </div>
                  ) : section.custom13 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        Please read the following arbitration agreement in this Section (
                        <strong>&ldquo;Arbitration Agreement&rdquo;</strong>) carefully. Unless you opt out in the
                        manner described in Section 13(9) below, this Arbitration Agreement requires you and Soul Media
                        Limited to resolve disputes by binding arbitration instead of in court, and limits the manner in
                        which we may seek relief from each other.
                      </p>
                      <p>
                        <strong className="text-gray-800">1. When Does This Arbitration Agreement Apply?</strong> This
                        Arbitration Agreement applies to any disputes or claims of any kind whatsoever (whether based in
                        contract, tort, statute, regulation, ordinance, fraud, misrepresentation or any other legal or
                        equitable theory) between you and Soul Media Limited arising out of or relating to the Terms,
                        prior versions of the Terms, your use of our App, or any other aspect of your relationship with
                        Soul Media Limited, including claims or disputes arising (but not actually filed in arbitration)
                        before the effective date of these Terms. It requires that, and by entering into these Terms you
                        and Soul Media Limited agree, that such disputes or claims will be resolved by binding
                        arbitration, rather than in court, except that (i) you or Soul Media Limited may assert
                        individual claims in small claims court if your claims qualify; and (ii) you or Soul Media
                        Limited may seek equitable relief in court for infringement or misuse of intellectual property
                        rights.
                      </p>
                      <p>
                        <strong className="text-gray-800">2. Notice of Dispute and Informal Resolution.</strong> Before
                        beginning the arbitration process, you and Soul Media Limited agree to first notify one another
                        of the dispute in writing at least 60 days in advance of initiating an arbitration. Notice to
                        Soul Media Limited must be sent by letter to our registered address: Flat 06, 15/F, Block D, Lai
                        Chi Kok Bay Garden, 272 Lai King Hill Road, Kwai Chung, NT, Hong Kong, and must provide your
                        name, current email address, mailing address, and telephone number, as well as the name, email
                        address, and telephone number associated with your Aurays account (if different from your
                        current information); and describe the nature of the claim and the specific relief being sought.
                      </p>
                      <p>
                        You and Soul Media Limited agree to meet and confer, via teleconference or videoconference, in a
                        good faith effort to informally resolve any claim or dispute covered by this Arbitration
                        Agreement. If either party is represented by counsel, that counsel may participate in the
                        informal dispute resolution conference. During this process, you may be provided with an offer
                        of judgment. A party defending against a claim may serve on an opposing party an offer to allow
                        judgment on specified terms, with the costs then accrued. If the judgment that the offeree
                        finally obtains is not more favourable than the unaccepted offer, the offeree must pay the costs
                        incurred after the offer was made.
                      </p>
                      <p>
                        All offers, promises, conduct, and statements made in the course of the informal dispute
                        resolution process by any party, its agents, employees, and attorneys are confidential and not
                        admissible for any purpose in any subsequent proceeding, provided that evidence that is
                        otherwise admissible or discoverable shall not be rendered inadmissible or non-discoverable as a
                        result of its use in the informal dispute resolution process.
                      </p>
                      <p>
                        The informal dispute resolution conference shall occur within 60 days of receipt of the written
                        notice described above, unless an extension is mutually agreed upon. If, after participating in
                        that conference, the parties are unable to resolve the dispute, the claimant may commence an
                        arbitration in accordance with this Agreement. Completion of this informal dispute resolution is
                        a condition precedent to filing any demand for arbitration. Failure to do so is a breach of this
                        Agreement, and no demand for arbitration shall be valid unless such written notice is provided
                        and good faith discussions have been conducted.
                      </p>
                      <p>
                        Any statute of limitations will be tolled while the parties engage in the informal dispute
                        resolution process described in this section.
                      </p>
                      <p>
                        <strong className="text-gray-800">3. How Do You Start The Arbitration Process?</strong> If you
                        and Soul Media Limited are unable to resolve the dispute within 60 days, either party may
                        proceed to file a claim for arbitration. To begin the arbitration process, you or Soul Media
                        Limited must submit notice by certified mail of the claim with an individualised arbitration
                        demand. To be valid, the demand must contain the name of the claiming party (you or Soul Media
                        Limited), your or Soul Media Limited&apos;s mailing address, the email address or phone number
                        associated with your Aurays account (if applicable), and a detailed description of the dispute
                        and the relief sought. Notice to Soul Media Limited must be submitted to our registered address:
                        Flat 06, 15/F, Block D, Lai Chi Kok Bay Garden, 272 Lai King Hill Road, Kwai Chung, NT, Hong
                        Kong.
                      </p>
                      <p>
                        <strong className="text-gray-800">4. What Are The Rules Of Arbitration?</strong>
                      </p>
                      <p>
                        (i) <strong>Governing Framework:</strong> This Arbitration Agreement and any dispute,
                        controversy, difference, or claim arising out of or relating to it shall be governed by,
                        interpreted, and enforced under the Arbitration Ordinance (Cap. 609, Laws of Hong Kong).
                      </p>
                      <p>
                        (ii) <strong>The Arbitration Provider and Rules:</strong> Any dispute, controversy, difference,
                        or claim arising out of or relating to these Terms, including the existence, validity,
                        interpretation, performance, breach, or termination thereof, shall be referred to and finally
                        resolved by arbitration administered by the Hong Kong International Arbitration Centre (
                        <strong>&ldquo;HKIAC&rdquo;</strong>) under the HKIAC Administered Arbitration Rules in force
                        when the Notice of Arbitration is submitted.
                      </p>
                      <p>
                        (iii) <strong>Seat and Language:</strong> The seat of arbitration shall be Hong Kong. The
                        tribunal shall consist of one (1) arbitrator appointed in accordance with the said HKIAC Rules.
                        The proceedings shall be conducted in the English language.
                      </p>
                      <p>
                        (iv) <strong>Small Claims and Consumer Exception:</strong> Notwithstanding subsections (i) and
                        (ii) above, if you are a user residing in Hong Kong, this Arbitration Agreement does not
                        restrict, waive, or otherwise deprive you of your statutory right to bring an eligible consumer
                        dispute before the Small Claims Tribunal of Hong Kong, provided that the claim falls squarely
                        within the statutory jurisdiction of that tribunal.
                      </p>
                      <p>
                        (v) <strong>Fees and Costs:</strong> The initiating party shall pay the initial filing fees
                        required by the HKIAC. Responsibility for the allocation of subsequent administrative fees,
                        arbitrator fees, and legal costs shall be determined by the arbitrator in accordance with the
                        HKIAC Rules and the Arbitration Ordinance (Cap. 609). If the arbitrator determines that a claim
                        or defense brought by a party is frivolous, malicious, or entirely without merit, the arbitrator
                        may order that party to fully reimburse the prevailing party for its portion of all
                        administrative, hearing, legal, and arbitrator fees incurred as a result of the frivolous claim.
                      </p>
                      <p>
                        <strong className="text-gray-800">5. What Can The Arbitrator Decide?</strong>
                      </p>
                      <p>
                        (i) <strong>Authority on Jurisdiction (Competence-Competence):</strong> The arbitral tribunal
                        shall have the exclusive authority to rule on its own jurisdiction, including any objections
                        with respect to the existence, validity, applicability, or enforceability of this Arbitration
                        Agreement, in accordance with Section 34 of the Arbitration Ordinance (Cap. 609, Laws of Hong
                        Kong).
                      </p>
                      <p>
                        (ii) <strong>Admissibility and Compliance:</strong> The arbitral tribunal shall have the
                        exclusive authority to determine whether any pre-conditions or conditions precedent to
                        arbitration have been complied with and to rule on the admissibility of any claims submitted to
                        arbitration.
                      </p>
                      <p>
                        (iii) <strong>Scope of Relief:</strong> The arbitrator shall have the authority to grant motions
                        dispositive of all or part of any claim. The arbitrator shall have the power to award monetary
                        damages and to grant any non-monetary remedy or interim relief available under Hong Kong law,
                        the HKIAC Administered Arbitration Rules, and these Terms. The arbitrator has the same authority
                        to award relief that a judge in a court of law would have.
                      </p>
                      <p>
                        (iv) <strong>Finality:</strong> The arbitrator will issue a written award and a statement of
                        decision describing the essential findings and conclusions on which the award is based,
                        including the calculation of any damages awarded. The award of the arbitrator shall be final and
                        binding upon you and us.
                      </p>
                      <p>
                        (v) <strong>Supervisory Jurisdiction:</strong> Any formal legal action, application, or
                        proceeding to stay court litigation in favor of arbitration, or any permitted appeal or
                        challenge to an award under the Arbitration Ordinance (Cap. 609), must be brought before the
                        High Court of the Hong Kong Special Administrative Region. The parties expressly submit to the
                        supervisory jurisdiction of the Hong Kong courts for the purposes of facilitating and supporting
                        the arbitral process.
                      </p>
                      <p>
                        <strong className="text-gray-800">6. Waver of Court Proceedings and Limited Review:</strong> By
                        agreeing to arbitration, you and Soul Media Limited are each agreeing to waive the right to have
                        disputes litigated in a court of law (except as explicitly provided in in these Terms 4
                        regarding the Small Claims Tribunal). Instead, you and the Company are electing that all claims
                        and disputes shall be resolved through final and binding arbitration administered under this
                        Arbitration Agreement. You acknowledge and agree that there is no judge or jury in arbitration
                        proceedings. An arbitrator can award the same individual damages and relief as a court and must
                        adhere to these Terms as a court would. However, you explicitly acknowledge that court review,
                        appeal, or set-aside of an arbitral award is strictly limited under the Arbitration Ordinance
                        (Cap. 609, Laws of Hong Kong), and opting for arbitration means trading the broader procedural
                        and appeal rights of formal court litigation for a more streamlined, private dispute resolution
                        process.
                      </p>
                      <p>
                        <strong className="text-gray-800">
                          7. Individual Dispute Resolution (No Multi-Party Actions):
                        </strong>{" "}
                        To the maximum extent permitted under applicable law, all claims, controversies, or disputes
                        within the scope of this Arbitration Agreement must be arbitrated or litigated solely on an
                        individual basis and not on a class, collective, consolidated, or representative basis. You and
                        Soul Media Limited explicitly agree that neither party may act as a representative or
                        participate as a member of any class or group of claimants in any dispute resolution proceeding.
                        Unless otherwise agreed in writing by both you and the Company, the arbitrator or court may not
                        consolidate, join, or coordinate the claims of more than one person or user, and may not
                        otherwise preside over any form of a consolidated or representative proceeding. The relief
                        awarded by the arbitrator or court shall be limited strictly to individual remedies justified by
                        that specific claimant&apos;s individual circumstances. If any court or arbitrator determines
                        that the limitations set forth above are illegal, void, or unenforceable as to a particular
                        claim or request for relief under Hong Kong law, then that specific claim or request for relief
                        shall be severed from the arbitration and must be brought before the courts of the Hong Kong
                        Special Administrative Region. Any remaining individual claims or requests for relief shall
                        continue to be resolved through binding arbitration pursuant to this Agreement.
                      </p>
                      <p>
                        <strong className="text-gray-800">8. Settlement Offers and Cost Consequences:</strong> At any
                        time prior to the commencement of the final arbitration hearing, either party may serve upon the
                        other party a written offer to settle all or part of the claims on specified terms, marked as a
                        &ldquo;Sanctioned Offer&rdquo; or made on a &ldquo;Without Prejudice Save as to Costs&rdquo;
                        basis. If a settlement offer made by one party is rejected or not accepted by the other party
                        within the timeframe specified in the offer, and the arbitration continues to a final
                        determination where the non-accepting party fails to obtain an award that is more favorable than
                        the terms of the settlement offer, that non-accepting party: (a) shall not be entitled to
                        recover any of its own legal costs or administrative expenses incurred after the date the offer
                        was made; and (b) shall be liable to pay the offering party&apos;s reasonable legal costs,
                        arbitrator fees, and institutional expenses incurred from the date the offer was served. The
                        arbitral tribunal shall have the full statutory power to enforce these cost consequences and
                        award interest on costs in accordance with Sections 69, 70, and 71 of the Arbitration Ordinance
                        (Cap. 609, Laws of Hong Kong). The existence of any settlement offer shall not be disclosed to
                        the arbitrator until all substantive issues of liability and damages have been determined.
                      </p>
                      <p>
                        <strong className="text-gray-800">9. Opt-Out:</strong> If you do not wish to be bound by the
                        Arbitration Agreement set forth in these Terms, you have the right to opt out. Opting out has no
                        adverse effect on your status as a user or your ability to use the App&apos;s premium features.
                        You must exercise your right to opt out within thirty-one (31) days from the date you first
                        create your account on the App or otherwise become subject to these Terms. To opt out, you must
                        send an unequivocal written notice of your decision to opt out via email to{" "}
                        <a href="mailto:support@aurays.app" className="text-[#7C55CC] font-semibold">
                          support@aurays.app
                        </a>
                        . Your opt-out email must include your full name, your registered App username or profile
                        handle; and the exact email address and/or mobile phone number you utilized to register and set
                        up your account. If you validly opt out of this Arbitration Agreement in accordance with the
                        steps above, neither you nor Soul Media Limited can compel the other to arbitrate disputes. Any
                        legal differences or claims arising between you and the Company must instead be brought before
                        the competent courts of the Hong Kong Special Administrative Region or the Small Claims
                        Tribunal, as applicable. All other non-arbitration sections of these Terms will continue to
                        apply to you in full force.
                      </p>
                      <p>
                        <strong className="text-gray-800">10. Severability.</strong> Except as provided in subsection
                        14(7), if any part or parts of this Arbitration Agreement are found under the law to be invalid
                        or unenforceable, then such specific part or parts shall be of no force and effect and shall be
                        severed and the remainder of the Arbitration Agreement shall continue in full force and effect.
                        If a court decides that any of the provisions in the Arbitration Agreement above is invalid or
                        unenforceable because it would prevent the exercise of a non-waivable right to pursue public
                        injunctive relief, then any dispute regarding the entitlement to such relief (and only that
                        relief) must be severed from arbitration and may be litigated in court. All other disputes
                        subject to arbitration under the terms of the Arbitration Agreement shall be arbitrated under
                        its terms.
                      </p>
                      <p>
                        <strong className="text-gray-800">12. Survival of Agreement.</strong> The terms of this
                        Arbitration Agreement will continue, even after your relationship with Soul Media Limited has
                        ended.
                      </p>
                    </div>
                  ) : section.custom14 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>
                        These Terms commence on the date you accept them (as described in the preamble) and continue
                        until terminated in accordance with the terms herein.
                      </p>
                      <p>
                        You can delete your Account at any time by logging into the App, going to the
                        &ldquo;Settings&rdquo; tab (the gear icon), and following the instructions to terminate your
                        account. Please note that if you delete your Account, your subscription will continue until the
                        end of the subscription period for which applicable fees have been paid, and you will not be
                        entitled to a refund except as stated in Section 5. In addition to deleting your account, you
                        will also need to cancel/manage any recurring subscriptions purchased via a Third Party Store to
                        avoid additional billing.
                      </p>
                      <p>
                        In the event that Soul Media Limited determines, in its sole discretion, that you have breached
                        any portion of these Terms, have misused the App, or have otherwise demonstrated conduct which
                        the Soul Media Limited regards as inappropriate or unlawful (whether on or off the App), Soul
                        Media Limited reserves the right to: (a) warn you via email (to any email addresses you have
                        provided to Soul Media Limited) that you have violated the Terms; (b) delete your User Content;
                        (c) discontinue your Account; (d) discontinue your subscription(s) without refund; (e) notify
                        and/or send your User Content to and/or fully cooperate with the proper law enforcement
                        authorities for further action; and/or (f) pursue to any other action which Soul Media Limited
                        deems to be appropriate. You agree that all terminations for cause shall be made in Soul Media
                        Limited&apos;s sole discretion and that Soul Media Limited shall not be liable to you or any
                        third party for any termination of your Account.
                      </p>
                      <p>
                        Termination of these Terms or your Account includes the removal of access to your Account, and
                        all related information and content associated with or inside your Account.
                      </p>
                      <p>
                        If your account is terminated by you or by the Soul Media Limited for any reason, all provisions
                        of these Terms which by their nature should survive, shall survive termination of these Terms,
                        including, without limitation, the Arbitration Agreement, ownership provisions, warranty
                        disclaimers and limitation of liability. Your information will be maintained and deleted in
                        accordance with our{" "}
                        <Link to="/privacy-policy" className="underline font-semibold">
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>
                  ) : section.custom15 ? (
                    <div className="flex flex-col gap-5 text-sm text-gray-500 leading-relaxed">
                      <p>There are a few more things we need to mention before you can use the App.</p>
                      <p>
                        These Terms, which we may amend from time to time, constitute the entire agreement between you
                        and Soul Media Limited. The Terms supersede all previous agreements, representations and
                        arrangements between us (written or oral), excluding the Privacy Policy. Nothing in this clause
                        shall limit or exclude any liability for fraudulent misrepresentation.
                      </p>
                      <p>
                        Soul Media Limited has taken reasonable steps to ensure the currency, availability, correctness
                        and completeness of the information contained on the App and provides that information on an
                        &ldquo;as is&rdquo;, &ldquo;as available&rdquo; basis. Soul Media Limited does not give or make
                        any warranty or representation of any kind about the information contained on Aurays, whether
                        express or implied. Use of the App and the materials available on it is at your sole risk. Soul
                        Media Limited is not responsible for any loss arising from the transmission, use of data, or
                        inaccurate Member Content.
                      </p>
                      <p>
                        You are responsible for taking all necessary precautions to ensure that any material you may
                        obtain from Aurays is free of viruses or other harmful components. You accept that Auryas will
                        not be provided uninterrupted or error free, that defects may not be corrected or that Soul
                        Media Limited, or the server that makes it available, are free of viruses or bugs, spyware,
                        Trojan horse or any similar malicious software. Soul Media Limited is not responsible for any
                        damage to your computer hardware, computer software, or other equipment or technology including,
                        but without limitation damage from any security breach or from any virus, bugs, tampering,
                        fraud, error, omission, interruption, defect, delay in operation or transmission, computer line
                        or network failure or any other technical or other malfunction.
                      </p>
                      <p>
                        The communications between you and Soul Media Limited may take place via electronic means,
                        whether you use the App or send Soul Media Limited emails, or whether Soul Media Limited posts
                        notices in the App or communicates with you via email. For contractual purposes, you (a) consent
                        to receive communications from Soul Media Limited in electronic form; and (b) agree that all
                        terms and conditions, agreements, notices, disclosures, and other communications that Soul Media
                        Limited provides to you electronically satisfy if it were to be in writing. The foregoing does
                        not affect your statutory rights.
                      </p>
                      <p>
                        As Aurays grows, we might have to make changes to these Terms so we reserve the right to modify,
                        amend or change the Terms at any time (a &ldquo;Change&rdquo;). If we do this then the Changes
                        will be posted on this page and we will indicate the Effective Date of the updates at the bottom
                        of the Terms. In certain circumstances, we may send an email to you notifying you of a Change.
                        It&apos;s also possible that we might ask you to agree to our Changes, but we&apos;ll let you
                        know. You should regularly check this page for notice of any Changes – we want our users to be
                        as informed as possible.
                      </p>
                      <p>
                        Your continued use of Aurays following any Change constitutes your acceptance of the Change and
                        you will be legally bound by the new updated Terms. If you do not accept any Changes to the
                        Terms, you should stop using Aurays immediately.
                      </p>
                      <p>
                        <strong className="text-gray-800">Additional items:</strong>
                      </p>
                      <p>
                        If, for any reason, any of the Terms are declared illegal, invalid or otherwise unenforceable by
                        a court of a competent jurisdiction, then to the extent that term is illegal, invalid or
                        unenforceable, it shall be severed and deleted from the Terms and the remainder of the Terms
                        shall survive, remain in full force and effect and continue to be binding and enforceable.
                      </p>
                      <p>
                        No failure or delay in exercising any right, power or privilege under the Terms shall operate as
                        a waiver of such right or acceptance of any variation of the Terms and nor shall any single or
                        partial exercise by either party of any right, power or privilege preclude any further exercise
                        of the right or the exercise of any other right, power or privilege.
                      </p>
                      <p>You represent and warrant that:</p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>
                          you are not located in a country that is subject to a U.S. Government embargo, or that has
                          been designated by the U.S. Government as a &ldquo;terrorist supporting&rdquo; country; and
                        </li>
                        <li>you are not listed on any U.S. Government list of prohibited or restricted parties.</li>
                      </ol>
                      <p>
                        By using the App, you agree and acknowledge that Bumble is a global app operating through
                        servers located in a number of countries around the world. If you live in a country with data
                        protection laws, the storage of your personal data may not provide you with the same protections
                        as you enjoy in your country of residence. By submitting your personal information, or by
                        choosing to upgrade the services you use, or by making use of the applications available on
                        Aurays, you agree to the transfer of your personal information to, and storage and processing of
                        your personal information in, any such countries and destinations.
                      </p>
                      <p>
                        The App may contain links to third-party websites or resources. In such cases, you acknowledge
                        and agree that we are not responsible or liable for:
                      </p>
                      <ol className="flex flex-col gap-2 pl-4 list-decimal">
                        <li>the availability or accuracy of such websites or resources; or</li>
                        <li>the content, products, or services on or available from such websites or resources.</li>
                      </ol>
                      <p>
                        Links to such websites or resources do not imply any endorsement. You acknowledge sole
                        responsibility for and assume all risk arising from your use of any such websites or resources.
                        Framing, in-line linking or other methods of association with the App are expressly prohibited
                        without first obtaining our prior written approval.
                      </p>
                      <p>
                        These Terms, and any rights and licences granted hereunder, may not be transferred or assigned
                        by you, but may be assigned by us without restriction.
                      </p>
                      <p>
                        In the event there is a discrepancy between this English language version and any translated
                        copies of the Terms, the English version shall prevail.
                      </p>
                      <p>
                        If you have any questions, complaints or claims with respect to the App, please contact us at{" "}
                        <a href="mailto:support@aurays.app" className="text-[#7C55CC] font-semibold">
                          support@aurays.app
                        </a>
                        .
                      </p>
                    </div>
                  ) : section.paragraphs && section.paragraphs.length > 0 ? (
                    <div className="flex flex-col gap-4">
                      {section.paragraphs.map((para, pi) => (
                        <p key={pi} className="text-sm text-gray-500 leading-relaxed">
                          {para}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-14 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#7C55CC] hover:text-[#4A2D8A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
